import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GetOrCreateError, GetOrCreateResponse, ResidentialService } from '@core/services/residential.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  pairwise,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { EMPTY, Observable, of, Subscription, throwError } from 'rxjs';
import {
  FAST_API_NON_GOOGLE_SOURCES,
  FastAPIAddress,
  FastAPIAddressesSource,
  FastAPIResponse,
  FastAPIService
} from '../fastapi.service';
import { ToastrService } from 'ngx-toastr';
import { PmaFirstForm, ResidentialForm } from 'src/app/core/interfaces/residential-data.interface';
import { AddressAttributes } from 'src/app/core/models/address.model';

// TODO: Remove when we will finally migrate to V2 Address Format
enum LegacyAddressesSource {
  OSM = 'OSM',
  Google = 'Google',
  GC = 'GC',
  None = 'None'
}

// TODO: Remove when we will finally migrate to V2 Address Format
interface LegacyFastApiAddress {
  city?: string;
  country?: string;
  county?: string;
  neighborhood?: string;
  full_str: string;
  description?: string;
  place_id?: string;
  lat?: number;
  lon?: number;
  state?: string;
  state_code?: number;
  street_name?: string;
  street_number?: string;
  zip_code?: string;
}

// TODO: Remove when we will finally migrate to V2 Address Format
interface LegacyLookupResponse {
  gc?: unknown[];
  google?: LegacyFastApiAddress[];
  osm?: LegacyFastApiAddress[];
}

@Component({
    selector: 'ui-address-search',
    templateUrl: './ui-address-search.component.html',
    styleUrls: ['./ui-address-search.component.scss'],
    standalone: false
})
export class UiAddressSearchComponent implements OnInit, OnDestroy {
  readonly PARTIAL_SEARCH_ERROR_CODE = 406;
  readonly SEARCH_MIN_LENGTH = 6;

  @Input() form: FormGroup;
  @Input() preserveRawAddress = false;
  @Output() setBuildingsFromAddress = new EventEmitter();
  @Output() setUnitsFromAddress = new EventEmitter();
  @Output() setAccountId = new EventEmitter();
  @Output() setCommunity = new EventEmitter();
  @Output() addressInvalidated = new EventEmitter<void>();

  @Input() addressFormatV2Enabled: boolean;

  addresses: FastAPIAddress[] | LegacyFastApiAddress[] = [];
  subscription: Subscription = new Subscription();
  loadingAddress = false;
  showAddressHint = false;

  continueEnteringHint = false;
// TODO: Remove when we will finally migrate to V2 Address Format
  private addressesSource: LegacyAddressesSource = LegacyAddressesSource.None;

  selectedAddress: string | FastAPIAddress | LegacyFastApiAddress;

  existingAddress: AddressAttributes | null = null;

  constructor(
    private residentialService: ResidentialService,
    private fastAPIService: FastAPIService,
    private toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.onAddressChanges();
    this.onAddressModification();
  }

  /**
   * Listens for any address text changes to detect when user modifies the selected address.
   * Clears insurable_id and selectedAddress immediately when user starts typing,
   * ensuring they must re-select from the dropdown.
   */
  private onAddressModification(): void {
    const sub = this.form
      .get('address')
      .valueChanges.pipe(startWith(this.form.get('address').value), pairwise())
      .subscribe(([prev, curr]) => {
        if (prev === curr) {
          return;
        }

        const isUserTyping = typeof curr === 'string';

        if (!isUserTyping) {
          return;
        }

        this.existingAddress = null;

        const selectedValue =
          typeof this.selectedAddress === 'string'
            ? this.selectedAddress
            : (this.selectedAddress as FastAPIAddress | LegacyFastApiAddress)?.full_str;

        const hasInsurableId = !!this.form.get('insurable_id')?.value;

        const typedDifferentFromSelected = selectedValue && curr !== selectedValue;
        const hasUntrackdInsurableId = hasInsurableId && !selectedValue;

        if (typedDifferentFromSelected || hasUntrackdInsurableId) {
          this.clearAddressSelection();
        }
      });

    this.subscription.add(sub);
  }

  /**
   * Clears the address selection state, insurable_id, and sets validation error.
   */
  private clearAddressSelection(): void {
    this.selectedAddress = null;
    this.addresses = [];

    const insurableIdControl = this.form.get('insurable_id');
    if (insurableIdControl?.value) {
      insurableIdControl.setValue(null);
    }

    this.form.get('address').setErrors({ addressNotSelected: true });

    this.addressInvalidated.emit();
  }

  onAddressChanges() {
    this.addresses = [];

    if (!this.addressFormatV2Enabled) {
      this.addressesSource = LegacyAddressesSource.None;
    }

    const sub = this.form
      .get('address')
      .valueChanges.pipe(
        debounceTime(800),
        distinctUntilChanged((prev, curr) => {
          const selectedFull =
            typeof this.selectedAddress === 'string'
              ? this.selectedAddress
              : (this.selectedAddress as FastAPIAddress | LegacyFastApiAddress)?.full_str;
          return (
            curr === prev ||
            (typeof curr === 'object' && curr?.full_str === selectedFull) ||
            curr === selectedFull
          );
        }),
        switchMap(() => of(this.form.get('address').value)),
        filter(search => search && typeof search === 'string' && search.length > 0),
        filter((term: string) => {
          const isValid = term.length >= this.SEARCH_MIN_LENGTH;
          this.continueEnteringHint = !isValid;
          return isValid;
        })
      )
      .subscribe(search => {
        this.loadingAddress = true;

        this.lookupAddresses(search)
          .pipe(
            tap(() => {
              this.showAddressHint = false;
              this.continueEnteringHint = false;
            }),
            finalize(() => {
              this.loadingAddress = false;
            }),
            catchError(err => {
              this.addresses = [];
              this.showAddressHint = false;
              this.continueEnteringHint =
                err?.originError?.status === this.PARTIAL_SEARCH_ERROR_CODE;
              return EMPTY;
            })
          )
          .subscribe(res => this.setAvailableAddresses(res));
      });

    this.subscription.add(sub);
  }

  selectAddress(address: FastAPIAddress | LegacyFastApiAddress) {
    this.selectedAddress = address?.full_str || address;

    this.form.get('address').setValue(address.full_str, { emitEvent: false });
    this.form.get('address').setErrors(null);
    this.getOrCreate(address);
  }

  selectExistingAddress(): void {
    if (!this.existingAddress) {
      return;
    }

    const fullAddress = this.existingAddress.full;
    this.existingAddress = null;

    this.selectedAddress = fullAddress;
    this.form.get('address').setValue(fullAddress, { emitEvent: false });
    this.form.get('address').setErrors(null);
    this.form.get('insurable_id').setValue(null);

    this.getOrCreateInsurable(this.form.value)
      .subscribe((result: GetOrCreateResponse) => this.handleGetOrCreateInsurableResponse(result));
  }

  validateStates(community) {
    const primaryAddress =
      community && community.primary_address ? community.primary_address : null;
    const state = primaryAddress && primaryAddress.state ? primaryAddress.state : null;
    if (state === 'AK') {
      this.form.get('address').setErrors({ invalidState: true });
    }
  }

  setBuildings(community) {
    const buildings = community && community.buildings ? community.buildings : [];
    this.setBuildingsFromAddress.emit(buildings);
  }

  setUnits(community) {
    const units = community && community.units ? community.units : [];
    this.setUnitsFromAddress.emit(units);
  }

  displayFnInsurable(address: FastAPIAddress | LegacyFastApiAddress | string) {
    return address && typeof address !== 'string' && (address as FastAPIAddress | LegacyFastApiAddress).full_str
      ? (address as FastAPIAddress | LegacyFastApiAddress).full_str
      : address
        ? (address as string)
        : '';
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private getOrCreate(address: FastAPIAddress | LegacyFastApiAddress): void {
    this.form.get('insurable_id').setValue(null);
    this.form.get('address').setErrors(null);

    const form = this.form.value;

    let request: Observable<GetOrCreateResponse>;

    if (this.addressFormatV2Enabled) {
      const fastAPIAddress = address as FastAPIAddress;
      if (FAST_API_NON_GOOGLE_SOURCES.includes(fastAPIAddress.source)) {
        request = this.getOrCreateInsurable(form);
      } else if (
        fastAPIAddress.source === FastAPIAddressesSource.Google &&
        fastAPIAddress.place_id
      ) {
        request = this.sendPlace(fastAPIAddress.place_id).pipe(
          tap(res => this.setFormValuesBasedOnGoogleSearch(res)),
          switchMap(res =>
            this.getOrCreateInsurable(res as unknown as PmaFirstForm | ResidentialForm)
          )
        );
      }
    } else {
      // TODO: Remove whole else block when we will finally migrate to V2 Address Format
      const legacyAddress = address as LegacyFastApiAddress;
      if (this.addressesSource === LegacyAddressesSource.OSM) {
        request = this.getOrCreateInsurable(form);
      } else if (
        this.addressesSource === LegacyAddressesSource.Google &&
        legacyAddress?.place_id
      ) {
        request = this.sendPlace(legacyAddress.place_id).pipe(
          tap(res => this.setFormValuesBasedOnGoogleSearch(res)),
          switchMap(res =>
            this.getOrCreateInsurable(res as unknown as PmaFirstForm | ResidentialForm)
          )
        );
      }
    }

    if (request) {
      request.subscribe((result: GetOrCreateResponse) =>
        this.handleGetOrCreateInsurableResponse(result)
      );
    } else {
      console.error('[UI Address Source] Address data source wasn\'t detected');
    }
  }

  private setFormValuesBasedOnGoogleSearch(res: FastAPIAddress | LegacyFastApiAddress): void {
    const county = this.form.get('county');
    const neighborhood = this.form.get('neighborhood');

    this.form.get('address')?.patchValue(res?.full_str, { emitEvent: false });

    if (county && res?.county) {
      county?.patchValue(res?.county, { emitEvent: false });
    }

    if (neighborhood && res?.neighborhood) {
      neighborhood?.patchValue(res?.neighborhood, { emitEvent: false });
    }
  }

  private getOrCreateInsurable(
    form: PmaFirstForm | ResidentialForm
  ): Observable<GetOrCreateResponse> {
    return this.residentialService.getOrCreateInsurable(form).pipe(
      catchError((err: GetOrCreateError) => {
        const message = `${err?.message}. ${err.payload.join(', ')}`;
        this.toastrService.error(message);
        return throwError(err);
      })
    );
  }

  private lookupAddresses(searchString: string): Observable<FastAPIResponse | LegacyLookupResponse> {
    return this.fastAPIService.lookup(searchString) as Observable<
      FastAPIResponse | LegacyLookupResponse
    >;
  }

  private sendPlace(placeId: string): Observable<FastAPIAddress | LegacyFastApiAddress> {
    return this.fastAPIService.sendPlace(placeId) as Observable<
      FastAPIAddress | LegacyFastApiAddress
    >;
  }

  private setAvailableAddresses(res: FastAPIResponse | LegacyLookupResponse): void {
    if (this.addressFormatV2Enabled) {
      const v2Response = res as FastAPIResponse;
      this.addresses = v2Response?.data || [];
    } else {
      // TODO: Remove whole else block when we will finally migrate to V2 Address Format
      const legacyResponse = res as LegacyLookupResponse;
      let addresses: LegacyFastApiAddress[] = [];

      const isOSMAvailable = legacyResponse?.osm && legacyResponse.osm.length > 0;
      const isGoogleAvailable = legacyResponse?.google && legacyResponse.google.length > 0;

      if (isOSMAvailable) {
        this.addressesSource = LegacyAddressesSource.OSM;
        addresses = legacyResponse.osm;
      }

      if (isGoogleAvailable && addresses.length === 0) {
        addresses = legacyResponse.google;
        this.addressesSource = LegacyAddressesSource.Google;

        addresses.forEach(address => {
          address.full_str = address?.description;
        });
      }

      this.addresses = addresses;
    }

    if (this.addresses.length === 0) {
      this.showAddressHint = true;
    }
  }

  private handleGetOrCreateInsurableResponse(result: GetOrCreateResponse): void {
    this.existingAddress = result?.existing_address || null;

    const community =
      result && result.results && result.results[0] ? result.results[0] : null;
    if (community) {
      this.validateStates(community);
      this.setCommunity.emit(community);
    }
    if (community && community.id) {
      this.form.get('insurable_id').setValue(community.id);

      if (this.preserveRawAddress && community?.primary_address) {
        this.form.get('raw_address').setValue(community.primary_address);
      }
    }
    const accountId = community && community.account_id ? community.account_id : null;
    if (accountId) {
      this.setAccountId.emit(accountId);
    }
    this.setBuildings(community);
    this.setUnits(community);
  }
}
